<template>
  <q-btn
    :bg-color="!$q.dark.isActive ? 'white' : 'black'"
    :disabled="(options.length === 0 && !optionsFunction) || loading || disable"
    class="q-py-sm"
    color="grey-5"
    data-html2canvas-ignore
    no-caps
    outline
  >
    <div
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
      style="font-weight: 400; font-size: small"
    >
      {{ label }}
      {{
        includedOptions.length || excludedOptions.length
          ? ` (${includedOptions.length + excludedOptions.length})`
          : ''
      }}
    </div>
    <q-menu
      flat
      max-width="600px"
      transition-duration="100"
      @before-show="loadOptions"
      @before-hide="resetTempSelections"
    >
      <q-card flat style="min-width: 250px">
        <q-card-section class="q-px-sm q-pt-none q-pb-md">
          <div class="q-mb-sm q-px-md q-pt-md">
            <div class="row items-center">
              <span class="title"> {{ title }} </span>
              <div>
                <q-icon class="q-ml-xs" name="info_outline" size="xs" />
                <q-tooltip v-if="tooltip">
                  {{ tooltip }}
                </q-tooltip>
              </div>
            </div>
            <div
              v-if="filterHelpText.plainText"
              :class="
                includedOptions.length > 0 || excludedOptions.length > 0
                  ? 'help-text-card'
                  : 'instructions-card'
              "
              class="row items-center"
            >
              <q-tooltip
                v-if="includedOptions.length > 0 || excludedOptions.length > 0"
                class="text-body2"
                max-width="250px"
              >
                {{ filterHelpText.plainText }}
                <b>
                  {{ filterHelpText.styledText }}
                </b>
              </q-tooltip>
              <p class="q-ma-none text-caption help-text">
                {{ filterHelpText.plainText }}
                <b>
                  {{ filterHelpText.styledText }}
                </b>
              </p>
            </div>
          </div>
          <div v-if="loading" class="q-pa-md">
            <q-linear-progress class="q-mt-md" color="primary" indeterminate />
          </div>
          <q-list v-else dense>
            <q-item
              v-for="option in sortedOptions"
              :key="option.value"
              class="row items-center button-trigger"
            >
              <q-btn-toggle
                :model-value="optionValue(option)"
                :options="[
                  {
                    slot: 'include',
                    value: true
                  },
                  {
                    slot: 'exclude',
                    value: false
                  }
                ]"
                class="q-mr-sm"
                clearable
                dense
                size="sm"
                style="outline: 1px solid grey"
                unelevated
                @update:model-value="handleOptionsUpdate(option, $event)"
              >
                <template #include>
                  <q-icon name="check" />
                  <q-tooltip :delay="800"> Include amenity </q-tooltip>
                </template>
                <template #exclude>
                  <q-icon name="close" />
                  <q-tooltip :delay="800"> Exclude amenity </q-tooltip>
                </template>
              </q-btn-toggle>
              <div class="flex row q-gutter-x-xs q-px-xs items-center">
                <span>{{ option.label }}</span>
                <span v-if="option.count" class="text-grey-7">({{ option.count }})</span>
              </div>
              <q-btn
                class="visible-on-hover q-ml-md q-mr-sm q-my-none"
                color="primary"
                dense
                flat
                style="min-width: 40px; font-size: 12px; line-height: 1"
                @click="selectOnly(option)"
              >
                Only
              </q-btn>
            </q-item>
          </q-list>
        </q-card-section>
        <div style="position: sticky; bottom: 0; z-index: 1; background-color: inherit">
          <q-separator />
          <q-card-actions align="right">
            <q-btn dense label="None" no-caps unelevated @click="setNone" />
            <q-btn dense label="All" no-caps unelevated @click="setAll" />
            <q-btn
              v-close-popup
              color="primary"
              dense
              label="Apply"
              no-caps
              unelevated
              @click="applyOptions"
            />
          </q-card-actions>
        </div>
      </q-card>
    </q-menu>
  </q-btn>
</template>

<script lang="ts">
import type { Option } from '@/components/SelectOptions.vue'
import type { PropType } from 'vue'
import { combineWords } from '@/utils'

export default {
  name: 'IncludeExcludeSelectOptions',
  props: {
    options: {
      type: Array as PropType<Option[]>,
      required: false,
      default: () => []
    },
    optionsFunction: {
      type: Function as PropType<() => Option[]>,
      required: false,
      default: null
    },
    label: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    tooltip: {
      type: String,
      required: false,
      default: ''
    },
    aliases: {
      type: Object as PropType<Record<string, string>>,
      required: false,
      default: () => ({})
    },
    includedOptions: {
      type: Array as PropType<Option[]>,
      required: false,
      default: () => []
    },
    excludedOptions: {
      type: Array as PropType<Option[]>,
      required: false,
      default: () => []
    },
    disable: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['apply'],
  data() {
    return {
      tempIncludedOptions: [] as Option[],
      tempExcludedOptions: [] as Option[],
      loadedOptions: [] as Option[],
      loading: false
    }
  },
  computed: {
    sortedOptions(): Option[] {
      return this.loadedOptions
        .toSorted((a, b) => a.value.localeCompare(b.value))
        .map((option) => {
          const alias = this.aliases[option.value]
          return {
            ...option,
            label: alias || option.label
          }
        })
    },
    filterHelpText(): { plainText: string; styledText: string } {
      const helpText = { plainText: '', styledText: '' }
      if (this.tempIncludedOptions.length === 0 && this.tempExcludedOptions.length === 0) {
        helpText.plainText = 'Select amenities that a unit must or must not have.'
        return helpText
      }
      if (this.tempIncludedOptions.length) {
        helpText.plainText =
          'Showing units that have ' +
          this.combineWords(this.tempIncludedOptions.map((_) => _.label))
      }
      if (this.tempIncludedOptions.length && this.tempExcludedOptions.length) {
        helpText.plainText =
          'Showing units that have ' +
          this.combineWords(this.tempIncludedOptions.map((_) => _.label))
        helpText.styledText =
          ' but do not have ' +
          this.combineWords(
            this.tempExcludedOptions.map((_) => _.label),
            'nor'
          )
        return helpText
      } else if (this.tempExcludedOptions.length) {
        helpText.plainText =
          'Showing units that do not have ' +
          this.combineWords(
            this.tempExcludedOptions.map((_) => _.label),
            'nor'
          )
      }
      return helpText
    }
  },
  watch: {
    options: {
      handler(newOptions: Option[]) {
        if (newOptions.length > 0 && !this.optionsFunction) {
          this.loadedOptions = newOptions
        }
      },
      immediate: true
    }
  },
  methods: {
    loadOptions() {
      this.resetTempSelections()
      if (this.optionsFunction) {
        this.loading = true
        try {
          // Use nextTick to ensure the menu is shown before potentially heavy computation
          this.$nextTick(() => {
            const result = this.optionsFunction()
            this.loadedOptions = result
            this.loading = false
          })
        } catch (error) {
          console.error('Error loading options:', error)
          this.loading = false
        }
      } else if (this.options.length > 0) {
        this.loadedOptions = this.options
      }
    },
    resetTempSelections() {
      // Reset temp selections to match the applied selections when menu closes without applying
      this.tempIncludedOptions = [...this.includedOptions]
      this.tempExcludedOptions = [...this.excludedOptions]
    },
    combineWords: combineWords,
    optionValue(option: Option): boolean | null {
      if (this.tempIncludedOptions.some((_) => _.value === option.value)) {
        return true
      }
      if (this.tempExcludedOptions.some((_) => _.value === option.value)) {
        return false
      }
      return null
    },
    setNone() {
      this.tempIncludedOptions = []
      this.tempExcludedOptions = []
      this.applyOptions()
    },
    setAll() {
      if (this.tempIncludedOptions.length === this.loadedOptions.length) {
        // all is already set
        return
      }
      this.tempIncludedOptions = []
      this.tempExcludedOptions = []
      for (const option in this.loadedOptions) {
        this.tempIncludedOptions.push(this.loadedOptions[option])
      }
      this.applyOptions()
    },
    handleOptionsUpdate(option: Option, event: boolean) {
      if (event === null) {
        if (this.optionValue(option)) {
          this.tempIncludedOptions.splice(
            this.tempIncludedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
        if (this.optionValue(option) === false) {
          this.tempExcludedOptions.splice(
            this.tempExcludedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
      } else if (event) {
        if (this.optionValue(option) === false) {
          this.tempExcludedOptions.splice(
            this.tempExcludedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
        this.tempIncludedOptions.push(option)
      } else {
        if (this.optionValue(option)) {
          this.tempIncludedOptions.splice(
            this.tempIncludedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
        this.tempExcludedOptions.push(option)
      }
    },
    applyOptions() {
      this.$emit('apply', { include: this.tempIncludedOptions, exclude: this.tempExcludedOptions })
    },
    selectOnly(option: Option) {
      this.tempExcludedOptions = []
      this.tempIncludedOptions = []
      this.tempIncludedOptions.push(option)
      this.applyOptions()
    }
  }
}
</script>

<style lang="scss" scoped>
.title {
  font-size: 17px;
  font-weight: 500;
}
.visible-on-hover {
  opacity: 0;
}
.button-trigger:hover .visible-on-hover {
  opacity: 1;
}
.body--dark {
  .help-text-card {
    border-left: $blue-4 solid 2px;
    background-color: $blue-10;
    color: $blue-1;
  }
  .instructions-card {
    border-left: $grey-5 solid 2px;
    background-color: $grey-9;
    color: $grey-2;
  }
}
.help-text-card {
  border-left: $blue-4 solid 2px;
  background-color: $blue-1;
  color: $blue-9;
  padding: 4px;
  margin-top: 2px;
}
.instructions-card {
  border-left: $grey-5 solid 2px;
  background-color: $grey-2;
  color: $grey-9;
  padding: 4px;
  margin-top: 2px;
}
.help-text {
  display: -webkit-box;
  min-height: 2rem;
  max-width: 250px;
  line-height: 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
